import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TimeUnit } from 'webapp/enums/enums';

import type { CheckboxItemInfo } from 'webapp/components/form/checkbox';
import FormCheckboxGroupComponent from 'webapp/components/form/checkbox-group';
import type { GroupSelected } from 'webapp/components/form/checkbox-group';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';

enum HolidaysType {
	NONE = 'none',
	COUNTRY = 'country',
}

interface Args {
	isDisabled: boolean;
	seasonality: TimeUnit[];
	allowedSeasonality: TimeUnit[];
	holidaysCountry: string | null;
	onChange: (seasonality: TimeUnit[], holidaysCountry: string) => void;
}

export default class ForecastsSettingsAdditionalSettingsComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;

	@tracked selectedSeasonality: TimeUnit[] = [];
	@tracked selectedHolidaysType = HolidaysType.NONE;
	@tracked selectedHolidaysCountry = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateFormFromArguments();
	}

	get showHelpArticle() {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	// ------ seasonality ------

	get seasonalityOptions() {
		const options: CheckboxItemInfo[] = [];

		const allowedSeasonality = this.args.allowedSeasonality || [];
		const disabledTooltipText =
			'Not enough historical data is available to detect this seasonality.';

		let disabled = !allowedSeasonality.includes(TimeUnit.WEEK);
		options.push({
			value: TimeUnit.WEEK,
			label: 'Weekly',
			disabled,
			tooltip: disabled ? disabledTooltipText : '',
		});

		disabled = !allowedSeasonality.includes(TimeUnit.MONTH);
		options.push({
			value: TimeUnit.MONTH,
			label: 'Monthly',
			disabled,
			tooltip: disabled ? disabledTooltipText : '',
		});

		disabled = !allowedSeasonality.includes(TimeUnit.YEAR);
		options.push({
			value: TimeUnit.YEAR,
			label: 'Yearly',
			disabled,
			tooltip: disabled ? disabledTooltipText : '',
		});

		return options;
	}

	@action
	changeSeasonality(seasonality: GroupSelected) {
		this.selectedSeasonality = seasonality as TimeUnit[];
		this.notifyUpdate();
	}

	// ------ holidays ------

	holidaysTypeOptions = [
		{ value: HolidaysType.NONE, label: 'None' },
		{ value: HolidaysType.COUNTRY, label: 'Country' },
	];

	get countriesOptions() {
		return this.constantsService.forecastHolidaysCountries;
	}

	get isHolidaysTypeNoneSelected() {
		return this.selectedHolidaysType === HolidaysType.NONE;
	}

	get isHolidaysTypeCountrySelected() {
		return this.selectedHolidaysType === HolidaysType.COUNTRY;
	}

	@action
	changeHolidaysType(type: AnySimpleValue) {
		this.selectedHolidaysType = type as HolidaysType;
		this.notifyUpdate();
	}

	@action
	changeHolidaysCountry(holidays: string) {
		this.selectedHolidaysCountry = holidays;
		this.notifyUpdate();
	}

	// ----- general -----

	@action
	updateFormFromArguments() {
		this.selectedSeasonality = this.args.seasonality || [];

		const hasHolidaysCountry = !!this.args.holidaysCountry;

		this.selectedHolidaysCountry =
			this.args.holidaysCountry || this.getDefaultHolidaysCountry();

		if (hasHolidaysCountry) {
			this.selectedHolidaysType = HolidaysType.COUNTRY;
		} else {
			this.selectedHolidaysType = HolidaysType.NONE;
		}
	}

	private getDefaultHolidaysCountry() {
		const allCountriesOptions = this.countriesOptions || [];
		const accountCountry = this.accountService.country;
		if (accountCountry && allCountriesOptions.find((c) => c.value === accountCountry)) {
			return accountCountry;
		} else {
			return 'US';
		}
	}

	private notifyUpdate() {
		const holidaysCountry =
			this.selectedHolidaysType === HolidaysType.COUNTRY ? this.selectedHolidaysCountry : '';

		this.args.onChange(this.selectedSeasonality, holidaysCountry);
	}
	<template>
		<div class="forecast-parameters-section">
			<div class="seasonality-section">
				<h5>Seasonality</h5>
				<p>
					Seasonality is a characteristic of a time series in which the data experiences
					regular and predictable changes that recur every period.
					{{#if this.showHelpArticle}}
						<a
							href="https://help.databox.com/databox-forecasting-methodology"
							target="_blank"
						>
							Learn more
						</a>
					{{/if}}
				</p>
				<FormCheckboxGroupComponent
					@name="seasonality"
					@selected={{this.selectedSeasonality}}
					@options={{this.seasonalityOptions}}
					@isDisabled={{@isDisabled}}
					@onChange={{this.changeSeasonality}}
				/>
			</div>

			<div class="holidays-section">
				<h5>Include holidays</h5>
				<p>
					Select a specific country and public holidays from the selection will be
					considered when calculating the forecast.
					{{#if this.showHelpArticle}}
						<a
							href="https://help.databox.com/databox-forecasting-methodology"
							target="_blank"
						>
							Learn more
						</a>
					{{/if}}
				</p>
				<FormRadiobuttonGroupComponent
					class="radiobutton-default"
					@name="holidays"
					@selected={{this.selectedHolidaysType}}
					@options={{this.holidaysTypeOptions}}
					@isDisabled={{@isDisabled}}
					@onChange={{this.changeHolidaysType}}
				/>
				{{#if this.isHolidaysTypeCountrySelected}}
					<div class="dd dd--country">
						<FormSelectComponent
							name="country"
							@selected={{this.selectedHolidaysCountry}}
							@options={{this.countriesOptions}}
							@onChange={{this.changeHolidaysCountry}}
							@isRequired={{true}}
							@isNumeric={{false}}
							@showFloatlabel={{false}}
							@isDisabled={{@isDisabled}}
						/>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
